/**
 * Client for the s2s TTS endpoints (tdd-0044 voice selection):
 *   GET  /v1/audio/voices  — list renderable voices
 *   POST /v1/audio/speech  — synthesize text → WAV blob (preview/demo)
 *
 * The voice service is the same one the realtime WS uses; its REST base is the
 * `wss://`/`ws://` `voiceApiUrl` mapped to `https://`/`http://`. Calls are authed
 * via the shared cookie (`credentials: 'include'`), matching the LB pattern.
 */

export interface VoiceOption {
  id: string;
  label: string;
  gender?: "female" | "male" | "neutral";
  accent?: string;
  description?: string;
}

/** Map a ws(s):// voice URL to its http(s):// REST origin. */
function httpBase(voiceApiUrl: string): string {
  return voiceApiUrl
    .replace(/^wss:\/\//, "https://")
    .replace(/^ws:\/\//, "http://")
    .replace(/\/$/, "");
}

/** Fetch the catalog of renderable voices. Returns [] on failure. */
export async function listVoices(voiceApiUrl: string): Promise<VoiceOption[]> {
  if (!voiceApiUrl) return [];
  try {
    const res = await fetch(`${httpBase(voiceApiUrl)}/v1/audio/voices`, {
      credentials: "include",
    });
    if (!res.ok) return [];
    const json = (await res.json()) as { data?: VoiceOption[] };
    return json.data ?? [];
  } catch {
    return [];
  }
}

/**
 * Synthesize `input` to a WAV blob using the given voice (or the engine default
 * when `voice` is undefined). Throws on failure so the caller can surface it.
 */
export async function synthesizeSpeech(
  voiceApiUrl: string,
  input: string,
  voice?: string,
): Promise<Blob> {
  const res = await fetch(`${httpBase(voiceApiUrl)}/v1/audio/speech`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ input, voice }),
  });
  if (!res.ok) {
    let message = `TTS failed (${res.status})`;
    try {
      const err = (await res.json()) as { message?: string };
      if (err.message) message = err.message;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  return res.blob();
}
